/**
 * Top-level "Action Failed" modal. Renders when actionError is non-null in
 * the shared useActionError store. Mounted from dashboard-page (outside the
 * map-overlay conditional) so it stays visible regardless of which view the
 * user is in.
 *
 * Special-case UI keys off actionError.code, not message text.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useActionError } from '@/stores/useActionError';
import { getArmPositions } from '@/lib/arm-api';

export function ActionErrorModal() {
  const actionError = useActionError((s) => s.actionError);
  const setActionError = useActionError((s) => s.setActionError);
  const runAction = useActionError((s) => s.runAction);
  const [recheckingArms, setRecheckingArms] = useState(false);

  if (actionError === null) return null;

  const isArmUnsafeError = actionError.code === 'ARMS_UNSAFE';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl p-10 w-[40rem] min-h-[16rem] flex flex-col gap-6 items-center justify-center text-center">
        <button
          onClick={() => setActionError(null)}
          aria-label="Dismiss"
          className="absolute top-4 right-4 p-1.5 rounded-lg text-orange-500 hover:bg-orange-500/10 transition-colors"
        >
          <X size={20} strokeWidth={2.5} />
        </button>
        <div className="flex flex-col gap-3 items-center">
          <p className="text-2xl font-semibold text-orange-500 leading-snug tracking-tight">
            Action Failed
          </p>
          {actionError.userMessage && (
            <p className="text-base text-foreground leading-relaxed max-w-md">
              {actionError.userMessage}
            </p>
          )}
        </div>
        {isArmUnsafeError && (
          <button
            disabled={recheckingArms}
            onClick={() => {
              setRecheckingArms(true);
              runAction(getArmPositions).finally(() => setRecheckingArms(false));
            }}
            className="rounded-lg bg-surface-muted px-5 py-2 text-sm font-medium text-foreground hover:bg-surface-strong transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {recheckingArms ? 'Rechecking…' : 'Recheck arm position'}
          </button>
        )}
      </div>
    </div>
  );
}
